import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { CreditCard, Check, AlertCircle, Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CloverPaymentForm({
  amount,           // in cents
  description,
  paymentType,      // 'registration', 'event', 'fundraiser'
  referenceId,
  onSuccess,
  onError,
  onCancel
}) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [success, setSuccess] = useState(null);
  const cloverRef = useRef(null);
  const cardNumberRef = useRef(null);
  const cardExpiryRef = useRef(null);
  const cardCvvRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  const initClover = useCallback(async () => {
    try {
      // Get config from backend
      const res = await axios.get(`${API_URL}/api/payments/config`, { withCredentials: true });
      const { public_key } = res.data;

      if (!public_key) {
        setError('Payment system not configured');
        setLoading(false);
        return;
      }

      // Load Clover SDK if not already loaded
      if (!window.Clover && !scriptLoadedRef.current) {
        scriptLoadedRef.current = true;
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.clover.com/sdk.js';
          script.async = true;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      // Initialize Clover
      const clover = new window.Clover(public_key);
      cloverRef.current = clover;
      const elements = clover.elements();

      // Mount card elements
      const cardNumber = elements.create('CARD_NUMBER');
      const cardExpiry = elements.create('CARD_DATE');
      const cardCvv = elements.create('CARD_CVV');

      cardNumber.mount('#card-number');
      cardExpiry.mount('#card-expiry');
      cardCvv.mount('#card-cvv');

      cardNumberRef.current = cardNumber;
      cardExpiryRef.current = cardExpiry;
      cardCvvRef.current = cardCvv;

      setLoading(false);
    } catch (err) {
      console.error('Clover init error:', err);
      setError('Failed to load payment form. Please try again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initClover();
    return () => {
      // Cleanup
      try {
        cardNumberRef.current?.destroy?.();
        cardExpiryRef.current?.destroy?.();
        cardCvvRef.current?.destroy?.();
      } catch {}
    };
  }, [initClover]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setProcessing(true);

    try {
      // Tokenize card via Clover
      const result = await cloverRef.current.createToken();

      if (result.errors) {
        const errorMessages = Object.values(result.errors).join(', ');
        setError(errorMessages || 'Invalid card details');
        setProcessing(false);
        return;
      }

      if (!result.token) {
        setError('Failed to process card. Please try again.');
        setProcessing(false);
        return;
      }

      // Charge via our backend
      const chargeRes = await axios.post(`${API_URL}/api/payments/charge`, {
        source: result.token,
        amount: amount,
        description: description,
        payment_type: paymentType,
        reference_id: referenceId
      }, { withCredentials: true });

      setSuccess(chargeRes.data);
      if (onSuccess) onSuccess(chargeRes.data);
    } catch (err) {
      // 503 from backend signals Clover auth/config is temporarily
      // unavailable. Render a clear, actionable state pointing the
      // parent to alternative payment methods rather than a generic
      // "Payment failed" toast.
      if (err.response?.status === 503) {
        setUnavailable(err.response?.data?.detail || 'Card payments are temporarily unavailable. Please try Venmo, Cash App, or Zelle, or contact the administrator.');
        setProcessing(false);
        return;
      }
      const msg = err.response?.data?.detail || 'Payment failed. Please try again.';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setProcessing(false);
    }
  };

  if (unavailable) {
    return (
      <div className="bg-white rounded-xl border border-yellow-300 overflow-hidden" data-testid="payment-unavailable">
        <div className="bg-yellow-500 px-5 py-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-white" />
          <span className="text-white font-bold">Card Payments Unavailable</span>
        </div>
        <div className="px-5 py-5 space-y-3">
          <p className="text-sm text-gray-800 leading-relaxed" data-testid="payment-unavailable-message">
            {unavailable}
          </p>
          <p className="text-xs text-gray-500">
            Your card was not charged. Please pick another payment method to continue your registration.
          </p>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onCancel}
              data-testid="payment-unavailable-back"
            >
              Back to Payment Methods
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="text-center py-8" data-testid="payment-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-bold text-navy text-xl mb-2">Payment Successful</h3>
        <p className="text-gray-600 mb-1">${(amount / 100).toFixed(2)} charged</p>
        {success.card_brand && success.card_last4 && (
          <p className="text-sm text-gray-500">{success.card_brand} ending in {success.card_last4}</p>
        )}
        <p className="text-xs text-gray-400 mt-2">Confirmation: {success.payment_id}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="clover-payment-form">
      {/* Header */}
      <div className="bg-navy px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-gold" />
          <span className="text-white font-bold">Secure Payment</span>
        </div>
        <div className="flex items-center gap-1.5 text-white/60 text-xs">
          <Lock className="w-3.5 h-3.5" />
          <span>Powered by Clover</span>
        </div>
      </div>

      {/* Amount */}
      <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-500">{description}</p>
        <p className="text-3xl font-black text-navy">${(amount / 100).toFixed(2)}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="payment-error">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-navy animate-spin" />
            <span className="ml-2 text-gray-500">Loading payment form...</span>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Card Number</label>
              <div
                id="card-number"
                className="h-12 border border-gray-300 rounded-lg px-3 flex items-center"
                data-testid="card-number-field"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Expiration</label>
                <div
                  id="card-expiry"
                  className="h-12 border border-gray-300 rounded-lg px-3 flex items-center"
                  data-testid="card-expiry-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">CVV</label>
                <div
                  id="card-cvv"
                  className="h-12 border border-gray-300 rounded-lg px-3 flex items-center"
                  data-testid="card-cvv-field"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={onCancel}
                  disabled={processing}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className="flex-1 bg-red hover:bg-red/90 text-white font-bold h-12"
                disabled={processing || loading}
                data-testid="pay-button"
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>Pay ${(amount / 100).toFixed(2)}</>
                )}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
